import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";

const CATS = [
  { key: "all", label: "Everything" },
  { key: "guide", label: "Guides" },
  { key: "lesson", label: "Lessons" },
  { key: "template", label: "Templates" },
  { key: "art", label: "Art" },
  { key: "print", label: "Prints" },
];

const catLabel = (k) => ({ guide: "Guide", lesson: "Lesson", template: "Template", art: "Art", print: "Print" }[k] || k);

const CraftsHome = () => {
  const { format } = useCurrency();
  const [items, setItems] = useState(null);
  const [cat, setCat] = useState("all");

  useEffect(() => {
    document.title = "NoirbyAmaraOri · Crafts";
    api.get("/crafts").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  const priceLabel = (c) => {
    const prices = (c.tiers || []).map((t) => Number(t.price)).filter((n) => !isNaN(n));
    if (prices.length) return `from ${format(Math.min(...prices))}`;
    return c.price != null ? format(c.price) : "";
  };

  const featured = useMemo(() => {
    if (!items) return null;
    return items.find((c) => c.featured) || items.find((c) => c.template === "cowrie") || items[0] || null;
  }, [items]);

  const grid = (items || []).filter((c) => cat === "all" || c.category === cat);

  return (
    <div data-testid="crafts-home">
      {/* Hero + featured product */}
      <section className="relative overflow-hidden border-b border-gold/10">
        <div className="pointer-events-none absolute -top-32 -right-24 w-[32rem] h-[32rem] rounded-full bg-gold/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-40 -left-24 w-[32rem] h-[32rem] rounded-full bg-wine/15 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-6 pt-16 pb-14">
          <motion.div initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}>
            <p className="text-[0.62rem] tracking-[0.4em] uppercase text-gold mb-5">{featured?.hero_eyebrow || "The Digital Atelier"}</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02] max-w-3xl">
              {featured?.hero_title || "Make What You've Been Saving"}
            </h1>
          </motion.div>

          {featured && (
            <motion.div
              initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.15 }}
              className="mt-12 grid lg:grid-cols-2 gap-8 lg:gap-14 items-center">
              <Link to={`/crafts/${featured.slug}`} data-testid="crafts-featured-image" className="block aspect-[4/5] bg-espresso/40 overflow-hidden group">
                {featured.cover_image
                  ? <img src={mediaUrl(featured.cover_image)} alt={featured.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  : <div className="w-full h-full flex items-center justify-center text-cream/20">No image</div>}
              </Link>
              <div data-testid="crafts-featured">
                <p className="text-[0.6rem] tracking-[0.3em] uppercase text-gold mb-3">{featured.hero_kicker || "Now in the shop"}</p>
                <h2 className="font-serif text-4xl sm:text-5xl leading-[1.02]">
                  {featured.title}{featured.subtitle ? <span className="block text-2xl italic text-cream/70 mt-2">{featured.subtitle}</span> : null}
                </h2>
                <p className="mt-5 text-cream/75 leading-relaxed max-w-md">
                  {featured.description || ""}
                </p>
                <p className="mt-5 font-serif text-xl text-cream tabular-nums">{priceLabel(featured)}</p>
                <Link to={`/crafts/${featured.slug}`} data-testid="crafts-featured-cta"
                  className="mt-6 inline-flex items-center gap-2 bg-gold text-cocoa px-8 py-3.5 text-[0.7rem] tracking-[0.2em] uppercase hover:bg-cream transition-colors">
                  Explore {featured.title} <ArrowRight size={15} />
                </Link>
                {(featured.stat_views || featured.stat_saves) && (
                  <div className="mt-8 flex items-center gap-6 text-cream/60" data-testid="crafts-social-proof">
                    {featured.stat_views && <div><span className="font-serif text-2xl text-gold">{featured.stat_views}</span><span className="ml-2 text-[0.62rem] tracking-[0.16em] uppercase">views</span></div>}
                    {featured.stat_saves && <><div className="w-px h-6 bg-cream/15" /><div><span className="font-serif text-2xl text-gold">{featured.stat_saves}</span><span className="ml-2 text-[0.62rem] tracking-[0.16em] uppercase">saves</span></div></>}
                    <span className="text-[0.62rem] tracking-[0.14em] uppercase text-cream/40">{featured.proof_label || "on the piece that inspired it"}</span>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Shop by category */}
      <section className="max-w-6xl mx-auto px-6 py-14">
        <div className="flex flex-wrap gap-2.5 mb-10">
          {CATS.map((t) => (
            <button key={t.key} onClick={() => setCat(t.key)} data-testid={`craft-filter-${t.key}`}
              className={`px-4 py-2 text-[0.64rem] tracking-[0.16em] uppercase border transition-colors ${
                cat === t.key ? "bg-gold text-cocoa border-gold" : "border-cream/20 text-cream/60 hover:border-gold hover:text-gold"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {items === null ? (
          <p className="text-cream/40 text-sm">Loading…</p>
        ) : grid.length === 0 ? (
          <p data-testid="crafts-cat-empty" className="text-cream/50 text-sm py-8">New {CATS.find((c) => c.key === cat)?.label.toLowerCase()} arriving soon.</p>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12">
            {grid.map((c) => (
              <Link key={c.id} to={`/crafts/${c.slug}`} data-testid={`craft-card-${c.slug}`} className="group block">
                <div className="aspect-[4/5] bg-espresso/40 overflow-hidden">
                  {c.cover_image
                    ? <img src={mediaUrl(c.cover_image)} alt={c.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    : <div className="w-full h-full flex items-center justify-center text-cream/20 text-xs">No image</div>}
                </div>
                <div className="pt-4">
                  {c.category && <p className="text-[0.58rem] tracking-[0.28em] uppercase text-gold mb-1.5">{catLabel(c.category)}</p>}
                  <h3 className="font-serif text-xl text-cream leading-snug">{c.title}</h3>
                  {c.subtitle && <p className="text-sm text-cream/50 mt-1 line-clamp-1">{c.subtitle}</p>}
                  <p className="text-sm text-cream/80 mt-2 tabular-nums">{priceLabel(c)}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Studio note */}
      <section className="border-t border-gold/15">
        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <p className="text-[0.62rem] tracking-[0.4em] uppercase text-gold mb-6">From the NoirbyAmaraOri Studio</p>
          <p className="font-serif text-xl sm:text-2xl text-cream/85 leading-relaxed">
            Every piece here began as something we made by hand and shared. These are the techniques, patterns and prints behind the work — offered so you can make them your own.
          </p>
        </div>
      </section>
    </div>
  );
};

export default CraftsHome;
